/** What each new-experiment step picked, as one line: for the review's summary and the step rail. */

import { PRODUCTION } from "@/features/connectors/types";
import { formatCount, plural } from "@/lib/format";
import { choiceRange } from "@/features/traces/period-picker";
import { formatPeriod } from "../detail/trace-run";
import { versionName } from "./pulls/pull-text";
import type { NewExperiment } from "./use-new-experiment";

const version = (v: string) => (v === PRODUCTION ? "production version" : `v${v}`);

export function datasetLine(flow: NewExperiment): string {
  const dataset = flow.dataset;
  if (!dataset) return "None";
  if (flow.live) {
    const { chosen: v, version: n } = flow.liveVersion;
    if (n != null) return `${dataset.name} · ${v ? versionName(v) : `v${n}`}`;
    if (flow.liveVersion.next) return `${dataset.name} · live · after v${flow.liveVersion.latest}`;
    return `${dataset.name} · live · ${formatPeriod(choiceRange(flow.period)) || "pick a period"}`;
  }
  return `${dataset.name}${flow.version ? ` · v${flow.version}` : ""}`;
}

/** The review's line on a live run's traces: fresh ones and how they are picked, or the version's. */
export function tracesLine(flow: NewExperiment, limit: number | undefined): string {
  const { chosen: v, version: picked } = flow.liveVersion;
  if (picked != null) return v ? versionName(v, true) : `v${picked}`;
  const n = limit ? formatCount(limit) : "";
  const { next, latest } = flow.liveVersion;
  return [
    "Fresh traces, pulled when the run starts",
    next ? `the next ${n || "ones"} after v${latest}` : `the first ${n || "ones"} from the period start`,
  ].join(" · ");
}

export function runnerLine(flow: NewExperiment, what: string): string {
  if (flow.runner === "outputs") return flow.live ? "The traces' own outputs" : "The outputs in the dataset";
  return [flow.connection?.name, what, version(flow.target.resolved.version)].filter(Boolean).join(" · ");
}

/** The rail's line per step, in `STEPS` order; empty while a step has nothing picked.
 *  The runner names the agent alone: a project is an id on some platforms. */
export function stepValues(flow: NewExperiment, open: number): string[] {
  const { form, target, dataset } = flow;
  const items = dataset?.latest_version?.item_count;
  return [
    form.name.trim(),
    dataset
      ? [datasetLine(flow), !flow.live && items ? `${formatCount(items)} items` : ""]
          .filter(Boolean)
          .join(" · ")
      : "",
    open > 2 ? runnerLine(flow, target.resolved.target ?? "") : "",
    open > 3 && flow.selected.size ? plural(flow.selected.size, "evaluator") : "",
    "",
    "Once, now",
  ];
}
